import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Mail, Lock, User, Eye, EyeOff, Check, Rocket, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { BoostaBolt, ZapBolt } from '@/components/BoostaBolt';
import { HoloCard } from '@/components/HoloCard';

const STEPS = [
  { key: 'name',     label: 'Your name',    icon: User, type: 'text',     placeholder: 'e.g. Ada Lovelace', testid: 'register-name' },
  { key: 'email',    label: 'Email',        icon: Mail, type: 'email',    placeholder: 'you@company.com',    testid: 'register-email' },
  { key: 'password', label: 'Password',     icon: Lock, type: 'password', placeholder: '6+ characters',      testid: 'register-password' },
];

export default function Register() {
  const { register, refreshUser } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [showPw, setShowPw] = useState(false);
  const [stage, setStage] = useState('form'); // form | plan
  const [plans, setPlans] = useState({});
  const [planBusy, setPlanBusy] = useState(null);

  const cur = STEPS[step];
  const val = form[cur.key];
  const canNext = cur.key === 'password' ? val.length >= 6 : val.trim().length > 0;

  useEffect(() => { api.get('/payments/plans').then(({data}) => setPlans(data.plans)).catch(()=>{}); }, []);

  const next = async () => {
    if (!canNext) return;
    if (step < STEPS.length - 1) return setStep(step + 1);
    setBusy(true);
    try { await register({ ...form, ref: searchParams.get('ref') || undefined }); toast.success('Account created — pick your plan.'); setStage('plan'); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const startTrial = async (planId) => {
    setPlanBusy(planId + '-trial');
    try {
      const { data } = await api.post('/payments/checkout', { package_id: planId, origin_url: window.location.origin, trial: true });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPlanBusy(null); }
  };
  const subscribe = async (planId) => {
    setPlanBusy(planId + '-pay');
    try {
      const { data } = await api.post('/payments/checkout', { package_id: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPlanBusy(null); }
  };

  const google = () => {
    const url = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(url)}`;
  };

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTilt({ x: ((e.clientX - r.left) / r.width - 0.5) * 6, y: ((e.clientY - r.top) / r.height - 0.5) * -6 });
  };

  if (stage === 'plan') {
    return (
      <div className="min-h-screen bg-boosta-ink px-4 py-14 relative overflow-hidden aurora">
        <div className="absolute inset-0 grid-bg opacity-30"/>
        <div className="absolute inset-0 grain"/>
        <div className="absolute top-6 left-6 z-10">
          <Link to="/" className="flex items-center gap-2"><BoostaBolt size={28} animate/><span className="font-display font-bold text-3d">Boosta</span></Link>
        </div>
        <div className="relative max-w-6xl mx-auto z-10" data-testid="register-plan-stage">
          <div className="text-center mb-10">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">Almost there</div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-3d">Choose your Boosta</h1>
            <p className="text-white/55 text-sm mt-3">Start a <span className="text-boosta-cyan font-bold">7-day free trial</span> or subscribe now. Cancel anytime.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {Object.entries(plans).map(([id, p]) => (
              <HoloCard key={id} className={`glass holo-edge rounded-2xl p-6 flex flex-col ${id==='pro'?'glow-cyan':''}`} data-testid={`register-plan-${id}`}>
                {id==='pro' && <div className="text-[9px] font-bold uppercase tracking-wider text-boosta-ink bg-boosta-cyan rounded-full px-2.5 py-0.5 self-start mb-3">Most popular</div>}
                <div className="font-display font-bold text-lg">{p.name}</div>
                <div className="text-[11px] text-white/45 mb-3 h-8">{p.tagline}</div>
                <div className="flex items-baseline gap-1">
                  <span className="font-display font-black text-4xl">£{p.price_gbp}</span>
                  <span className="text-white/40 text-xs">/{p.interval}</span>
                </div>
                <ul className="mt-4 space-y-2 flex-1">
                  {p.benefits.map((b,i)=>(
                    <li key={b} className="flex items-start gap-2 text-xs text-white/70"><Check className="w-3.5 h-3.5 text-boosta-emerald mt-0.5 shrink-0"/>{b}</li>
                  ))}
                </ul>
                <button disabled={!!planBusy} onClick={()=>startTrial(id)} data-testid={`register-trial-${id}`}
                  className="mt-5 w-full btn-primary rounded-xl py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  {planBusy===id+'-trial' ? 'Starting…' : <><Rocket className="w-4 h-4"/> Start free trial</>}
                </button>
                <button disabled={!!planBusy} onClick={()=>subscribe(id)} data-testid={`register-subscribe-${id}`}
                  className="mt-2 w-full btn-ghost rounded-xl py-2 text-xs font-medium disabled:opacity-50">
                  {planBusy===id+'-pay' ? 'Redirecting…' : 'Subscribe & pay now'}
                </button>
              </HoloCard>
            ))}
          </div>
          <div className="text-center mt-8">
            <button onClick={()=>nav('/dashboard')} data-testid="register-skip-plan" className="text-sm text-white/45 hover:text-boosta-cyan transition inline-flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5"/> Skip — explore on the Free plan (250 ZAP)
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-boosta-ink flex items-center justify-center px-4 relative overflow-hidden aurora">
      <div className="absolute inset-0 grid-bg opacity-30"/>
      <div className="absolute inset-0 grain"/>
      {[...Array(6)].map((_, i) => (
        <motion.div key={`float-bolt-${i}`} className="absolute opacity-[0.08] pointer-events-none"
          initial={{ x: `${20 + i * 12}%`, y: `${10 + (i % 3) * 25}%`, rotate: i * 40 }}
          animate={{ y: [`${10 + (i%3)*25}%`, `${18 + (i%3)*25}%`, `${10 + (i%3)*25}%`], rotate: [i*40, i*40+8, i*40] }}
          transition={{ duration: 6 + i, repeat: Infinity, ease: 'easeInOut' }}>
          <BoostaBolt size={80 + (i%3)*40}/>
        </motion.div>
      ))}

      <div className="absolute top-6 left-6 z-10">
        <Link to="/" className="flex items-center gap-2"><BoostaBolt size={28} animate/><span className="font-display font-bold text-3d">Boosta</span></Link>
      </div>

      <motion.div className="relative w-full max-w-md z-10"
        onMouseMove={onMove} onMouseLeave={()=>setTilt({x:0,y:0})}
        style={{ perspective: 1400 }}>
        <motion.div className="glass rounded-3xl p-8 md:p-10 shadow-[0_40px_120px_-20px_rgba(0,240,255,0.35)]"
          animate={{ rotateY: tilt.x, rotateX: tilt.y }} transition={{ type: 'spring', stiffness: 120, damping: 15 }}
          style={{ transformStyle: 'preserve-3d' }}>
          <div className="flex items-center gap-3 mb-6" data-testid="register-progress">
            {STEPS.map((_, i) => (
              <div key={`progress-step-${i}`} className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden relative">
                <motion.div initial={{ width: 0 }} animate={{ width: i <= step ? '100%' : '0%' }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, #E5FF00, #00F0FF)' }}/>
              </div>
            ))}
            <motion.div animate={{ rotate: (step + 1) * 90, scale: 1 + step * 0.06 }} transition={{ type:'spring', stiffness:150 }}>
              <BoostaBolt size={22} animate/>
            </motion.div>
          </div>

          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">Step {step+1} of {STEPS.length}</div>
          <h1 className="font-display font-black text-3xl tracking-tight text-3d">Create your Boosta</h1>
          <p className="text-white/50 text-sm mt-1.5">7-day trial · 250 ZAP included</p>

          <AnimatePresence mode="wait">
            <motion.div key={cur.key}
              initial={{ opacity: 0, rotateY: 25, x: 30 }}
              animate={{ opacity: 1, rotateY: 0, x: 0 }}
              exit={{ opacity: 0, rotateY: -25, x: -30 }}
              transition={{ duration: 0.35, ease: [0.22,1,0.36,1] }}
              className="mt-8" style={{ transformStyle:'preserve-3d' }}>
              <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block">{cur.label}</label>
              <div className="relative group" style={{ transform: 'translateZ(24px)' }}>
                <cur.icon className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 group-focus-within:text-boosta-cyan transition"/>
                <input autoFocus required type={cur.type === 'password' && showPw ? 'text' : cur.type} value={val}
                  onChange={e=>setForm(p=>({...p,[cur.key]:e.target.value}))}
                  onKeyDown={e=>{ if (e.key==='Enter') next(); }}
                  data-testid={cur.testid} placeholder={cur.placeholder}
                  className={`w-full bg-black/40 border border-white/10 rounded-xl pl-10 ${cur.type==='password'?'pr-11':'pr-3'} py-3.5 text-sm focus:border-boosta-cyan/60 focus:shadow-[0_0_0_1px_rgba(0,240,255,0.3),0_10px_30px_-10px_rgba(0,240,255,0.35)] outline-none transition`}/>
                {cur.type === 'password' && (
                  <button type="button" onClick={()=>setShowPw(v=>!v)} data-testid="register-toggle-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-boosta-cyan transition" aria-label={showPw?'Hide password':'Show password'}>
                    {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                  </button>
                )}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-6 flex gap-3" style={{ transformStyle:'preserve-3d' }}>
            {step > 0 && (
              <button onClick={()=>setStep(step-1)} className="btn-ghost rounded-xl px-5 py-3 text-sm" data-testid="register-back">Back</button>
            )}
            <motion.button whileTap={{ scale: 0.96, y: 2 }} whileHover={{ y: -2 }}
              disabled={!canNext || busy} onClick={next} data-testid="register-submit"
              className="flex-1 btn-primary rounded-xl py-3.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40">
              {busy ? 'Creating…' : (step === STEPS.length-1 ? <>Continue to plans <ArrowRight className="w-4 h-4"/></> : <>Continue <ArrowRight className="w-4 h-4"/></>)}
            </motion.button>
          </div>

          <div className="flex items-center gap-3 my-6 text-[10px] uppercase tracking-[0.2em] text-white/30">
            <div className="flex-1 h-px bg-white/10"/> Or <div className="flex-1 h-px bg-white/10"/>
          </div>
          <motion.button whileTap={{ scale: 0.97 }} whileHover={{ y: -2 }}
            onClick={google} data-testid="register-google" className="w-full btn-ghost rounded-xl py-3 text-sm font-medium">
            Sign up with Google
          </motion.button>

          <div className="mt-7 text-center text-sm text-white/45">
            Already on Boosta? <Link to="/login" className="text-boosta-cyan hover:underline">Sign in</Link>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
